import React, { useState, useRef, FormEvent, useEffect, useCallback } from "react";

import { DeviceEnum } from "../../types/context-detection";
import { DeviceScrollEnum } from "../../types/suggestions";
import { IHandleKeyboardNavigation } from "../../types/keyboard-navigation";
import { IPersonalizedSuggestions, ISuggestionResponse } from "../../types/suggestion-response";
import { SearchBoxProps } from "../../types/searchbox";

import debounce from "../../util/debounce";
import { defaultSuggestionResponse } from "../../util/suggestions/defaultSuggestionResponse";
import { L10nContext, NavigationContext } from "../../util/contexts";
import { PageViewLogDataId } from "../../util/pageViewLogDataId";
import { sendKrakenEvent } from "../../util/events/kraken";
import { setStoredSearchQuery } from "../../util/queryStorage";
import { subscribeToPriceMode, getCurrentPriceMode, PriceMode } from "../../util/priceModeObserver";
import { useKeyboardNavigation } from "../../util/suggestions/keyboard-navigation";
import { useL10n } from "../../util/l10n";
import { usePersonalizedSuggestions } from "../../util/personalization/usePersonalizedSuggestions";

import Modal from "../modal/modal";
import PersonalizedSuggestionColumns from "../suggestion-list/personalized-suggestion-columns";
import SearchIcon from "../../Assets/svg/Search_2021_icon";
import SuggestionColumns from "../suggestion-list/suggestion-columns";

import "./searchbox-wide.scss";
import "../suggestion-list/suggestions-wide.scss";

const SearchBoxWide = ({ detectedContext, ...props }: SearchBoxProps) => {
    const { isWebShop, isMobileShop, device, customerType } = detectedContext;
    const { personalizedData, isLoading, isEnabled } = usePersonalizedSuggestions();
    const SEARCH_TERM_LENGTH_LIMIT = 50;
    const TOP_SEARCH_QUERIES_LIMIT = isWebShop ? 10 : 5;
    const inputTabNavAttribute = { focusable: true };

    const [focus, setFocus] = useState(false);
    const [input, setInput] = useState("");
    const [priceMode, setPriceMode] = useState<PriceMode>("gross");
    const [validSearchTerm, setValidSearchTerm] = useState("");
    const [hasValidSearchTerm, setHasValidSearchTerm] = useState(false);
    const [deviceScrollClass, setDeviceScrollClass] = useState(DeviceScrollEnum.None);
    const [showModal, setShowModal] = useState(false);

    const [isPersonalizationAvailable, setIsPersonalizationAvailable] = useState(false);

    // for ShopSearchEnableZeroQuerySuggestions off:
    const [hasSuggestions, setHasSuggestions] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [suggestions, setSuggestions] = useState<ISuggestionResponse>(
        defaultSuggestionResponse()
    );
    const keyboardNavElementRef = useRef<IHandleKeyboardNavigation | null>(null);

    // for ShopSearchEnableZeroQuerySuggestions on:
    const [hasPersonalizedSuggestions, setHasPersonalizedSuggestions] = useState(false);
    const [showPersonalizedSuggestions, setShowPersonalizedSuggestions] = useState(false);
    const [personalizedSuggestions, setPersonalizedSuggestions] =
        useState<IPersonalizedSuggestions>(personalizedData);
    const keyboardNavPersonalizedElementRef = useRef<IHandleKeyboardNavigation | null>(null);

    const searchBoxRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const focusRef = useRef(focus);
    const suggestionsRef = useRef(suggestions);
    const hasValidSearchTermRef = useRef(false);
    const touchScrollElementRef = useRef<HTMLDivElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const pageViewLogDataId = PageViewLogDataId.get();

    const l10n = useL10n(props.l10n);

    useEffect(() => {
        focusRef.current = focus;
    }, [focus]);

    useEffect(() => {
        const isValidSearchTerm = validSearchTerm !== "";
        hasValidSearchTermRef.current = isValidSearchTerm;
        setHasValidSearchTerm(isValidSearchTerm);
    }, [validSearchTerm]);

    useEffect(() => {
        const isAvailable = !isLoading && isEnabled;
        setPersonalizedSuggestions({ ...personalizedData });
        setIsPersonalizationAvailable(isAvailable);
    }, [isLoading, isEnabled, personalizedData]);

    useEffect(() => {
        const isValidSearchWithoutSuggestions = !hasPersonalizedSuggestions && hasValidSearchTerm;
        const shouldBeShown =
            isPersonalizationAvailable && focus && !isValidSearchWithoutSuggestions;
        setShowPersonalizedSuggestions(shouldBeShown);
    }, [isPersonalizationAvailable, hasPersonalizedSuggestions, hasValidSearchTerm, focus]);

    useEffect(() => {
        setPriceMode(getCurrentPriceMode());
        const unsubscribe = subscribeToPriceMode((priceMode) => setPriceMode(priceMode));
        return unsubscribe;
    }, []);

    useEffect(() => {
        suggestionsRef.current = suggestions;
    }, [suggestions]);

    useEffect(() => {
        const hasSuggestions =
            suggestions.articles.length > 0 ||
            suggestions.flexibleSets.length > 0 ||
            suggestions.contents.length > 0 ||
            suggestions.topSearchQueries.length > 0;
        setHasSuggestions(hasSuggestions);
    }, [suggestions]);

    useEffect(() => {
        const hasPersonalizedData =
            personalizedSuggestions.articles.length > 0 ||
            personalizedSuggestions.flexibleSets.length > 0 ||
            personalizedSuggestions.contents.length > 0 ||
            personalizedSuggestions.topSearchQueries.length > 0 ||
            personalizedSuggestions.storedQueries.length > 0;
        setHasPersonalizedSuggestions(hasPersonalizedData);
    }, [personalizedSuggestions]);

    useEffect(() => {
        const shouldBeShown = hasSuggestions && focus && hasValidSearchTerm;
        setShowSuggestions(shouldBeShown);
    }, [hasSuggestions, focus, hasValidSearchTerm]);

    useEffect(() => {
        if (showSuggestions && touchScrollElementRef.current) {
            // enables touch scroll on parallax pages
            if (device === DeviceEnum.Kiosk || device === DeviceEnum.Tablet) {
                touchScrollElementRef.current.ontouchstart = (e) => e.stopPropagation();
                touchScrollElementRef.current.ontouchend = (e) => e.stopPropagation();
                touchScrollElementRef.current.ontouchmove = (e) => e.stopPropagation();
            }
        }
    }, [showSuggestions, device]);

    useEffect(() => {
        if (showPersonalizedSuggestions && touchScrollElementRef.current) {
            // enables touch scroll on parallax pages
            if (device === DeviceEnum.Kiosk || device === DeviceEnum.Tablet) {
                touchScrollElementRef.current.ontouchstart = (e) => e.stopPropagation();
                touchScrollElementRef.current.ontouchend = (e) => e.stopPropagation();
                touchScrollElementRef.current.ontouchmove = (e) => e.stopPropagation();
            }
        }
    }, [showPersonalizedSuggestions, device]);

    const handleClick = useCallback((event: Event) => {
        const target = event.target as HTMLElement;

        if (searchBoxRef.current && !searchBoxRef.current.contains(target) && focusRef.current) {
            handleBlur();
        }
    }, []);

    useEffect(() => {
        if (DeviceEnum.Kiosk === device) {
            setDeviceScrollClass(DeviceScrollEnum.Kiosk);
        } else if (DeviceEnum.Tablet === device) {
            setDeviceScrollClass(DeviceScrollEnum.Tablet);
        } else if (DeviceEnum.Desktop === device) {
            setDeviceScrollClass(DeviceScrollEnum.Desktop);
        }
    }, [device]);

    useEffect(() => {
        document.addEventListener("mousedown", handleClick);

        return () => {
            document.removeEventListener("mousedown", handleClick);
        };
    }, [handleClick]);

    const handleSubmit = useCallback(
        (e: FormEvent<HTMLFormElement> | KeyboardEvent) => {
            e.preventDefault();

            const trimmedInput = input.trim();
            if (!trimmedInput) {
                handleBlur();
                setShowModal(true);
                setShowSuggestions(false);
                setShowPersonalizedSuggestions(false);
                return;
            }

            const target = `${props.searchTarget}?query=${encodeURIComponent(trimmedInput)}`;
            if (isPersonalizationAvailable) setStoredSearchQuery({ name: trimmedInput, target });
            window.location.href = target;
        },
        [input, props.searchTarget, isPersonalizationAvailable]
    );

    const personalizedNavApi = useKeyboardNavigation(
        () => keyboardNavPersonalizedElementRef.current?.getNavigationElements() ?? []
    );

    const navigationApi = useKeyboardNavigation(
        () => keyboardNavElementRef.current?.getNavigationElements() ?? []
    );

    const handleKeyPressedWhileFocused = useCallback(
        (event: KeyboardEvent) => {
            const navApi = isPersonalizationAvailable ? personalizedNavApi : navigationApi;
            const activeNavRef = isPersonalizationAvailable
                ? keyboardNavPersonalizedElementRef
                : keyboardNavElementRef;

            if (event.key === "Tab" || (event.shiftKey && event.key === "Tab")) {
                handleBlur();
            } else if (
                (event.key === "Escape" || event.key === "Esc") &&
                activeNavRef.current !== null
            ) {
                handleBlur();
                shell?.tabNav.focusNext();
            } else if (event.key === "Enter" && !navApi.hasSelectedNavElement()) {
                handleSubmit(event);
            } else if (device === DeviceEnum.Desktop) {
                navApi.handleKeyDown(event);
            }
        },
        [device, navigationApi, personalizedNavApi, isPersonalizationAvailable, handleSubmit]
    );

    useEffect(() => {
        const input = inputRef.current;
        if (!input) return;

        if (focus) {
            input.addEventListener("keydown", handleKeyPressedWhileFocused);
        } else {
            input.removeEventListener("keydown", handleKeyPressedWhileFocused);
        }

        return () => {
            input.removeEventListener("keydown", handleKeyPressedWhileFocused);
        };
    }, [focus, handleKeyPressedWhileFocused]);

    const handleModalKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (showModal && (event.key === "Escape" || event.key === "Esc")) {
                event.preventDefault();
                setShowModal(false);
                inputRef.current?.focus();
            }
        },
        [showModal]
    );

    useEffect(() => {
        document.addEventListener("keydown", handleModalKeyDown);

        return () => {
            document.removeEventListener("keydown", handleModalKeyDown);
        };
    }, [handleModalKeyDown]);

    useEffect(() => {
        const suggestionsVisible = hasSuggestions && showSuggestions;
        const hasElements = keyboardNavElementRef.current?.hasNavigationElements();
        if (!suggestionsVisible && hasElements) {
            navigationApi.resetNavigation();
        }
    }, [hasSuggestions, showSuggestions, navigationApi]);

    useEffect(() => {
        const suggestionsVisible = hasPersonalizedSuggestions && showPersonalizedSuggestions;
        const hasElements = keyboardNavPersonalizedElementRef.current?.hasNavigationElements();
        if (!suggestionsVisible && hasElements) {
            personalizedNavApi.resetNavigation();
        }
    }, [hasPersonalizedSuggestions, showPersonalizedSuggestions, personalizedNavApi]);

    const resetPersonalizedSuggestionResponse = () => {
        setPersonalizedSuggestions({ ...personalizedData });
    };

    const resetSuggestionResponse = () => {
        setSuggestions(defaultSuggestionResponse());
    };

    const fetchSuggestions = async (input: string) => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        const headers: Record<string, string> = pageViewLogDataId
            ? { "X-PageView-ID": pageViewLogDataId }
            : {};

        try {
            const res = await fetch(
                `${props.apiUrl}suggestion/?searchTerm=${encodeURIComponent(
                    input
                )}&topSearchQueriesLimit=${TOP_SEARCH_QUERIES_LIMIT}`,
                { headers, signal: controller.signal }
            );

            if (!res.ok) throw new Error("Network response was not ok");

            const response = await res.json();

            if (
                response.articles.length ||
                response.flexibleSets.length ||
                response.contents.length ||
                response.topSearchQueries.length
            ) {
                const newSuggestions = response as ISuggestionResponse;

                const handleEmptySearchTermEdgeCase = !hasValidSearchTermRef.current;
                if (handleEmptySearchTermEdgeCase) return;

                if (JSON.stringify(newSuggestions) !== JSON.stringify(suggestionsRef.current)) {
                    sendKrakenEvent(input);
                }

                setSuggestions(newSuggestions);
                setPersonalizedSuggestions({
                    ...newSuggestions,
                    isSuggestionResponse: true,
                    storedQueries: []
                });
            } else {
                resetSuggestionResponse();
                resetPersonalizedSuggestionResponse();
            }
        } catch (error) {
            if (error instanceof Error && error.name !== "AbortError") {
                resetSuggestionResponse();
                resetPersonalizedSuggestionResponse();
            }
        } finally {
            abortControllerRef.current = null;
        }
    };

    const debouncedFetch = useRef<((input: string) => void) & { cancel: () => void }>(
        debounce((input: string) => {
            fetchSuggestions(input);
        }, 250)
    );

    const sanatizeInput = (input: string) => {
        const validInput = input.length > SEARCH_TERM_LENGTH_LIMIT ? input.slice(0, 50) : input;
        return validInput;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value ?? "";
        const sanatizedInput = sanatizeInput(value);
        setInput(sanatizedInput);

        const trimmedInput = sanatizedInput.trim();
        if (trimmedInput === "") {
            setValidSearchTerm("");
            debouncedFetch.current.cancel();
            resetSuggestionResponse();
            resetPersonalizedSuggestionResponse();
        } else {
            setValidSearchTerm(trimmedInput);
            debouncedFetch.current(trimmedInput);
        }
    };

    const handleFocus = () => {
        setFocus(true);
    };

    const hideModal = () => {
        setShowModal(false);
        inputRef.current?.focus();
    };

    function handleBlur(): void {
        setFocus(false);
        inputRef.current?.blur();
    }

    return (
        <L10nContext.Provider value={l10n}>
            <div
                className={
                    "searchbox wide" +
                    ` ssw-current-pricemode-${priceMode}` +
                    ` ${props.tenant}` +
                    ` ${focus ? "focus" : ""}`
                }
                ref={searchBoxRef}
            >
                <div className="search">
                    <form
                        method="get"
                        action={props.searchTarget}
                        acceptCharset="utf-8"
                        onSubmit={handleSubmit}
                    >
                        <input
                            ref={inputRef}
                            value={input}
                            maxLength={50}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            placeholder={validSearchTerm || focus ? "" : l10n.defaultTerm}
                            className="ssw-input"
                            type="text"
                            name="query"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="none"
                            spellCheck="false"
                            aria-label={l10n.defaultTerm}
                            {...inputTabNavAttribute}
                        />
                        <button className="ssw-button" type="submit" aria-label={l10n.defaultTerm}>
                            <SearchIcon />
                        </button>
                    </form>
                </div>

                {isPersonalizationAvailable
                    ? showPersonalizedSuggestions && (
                          <NavigationContext.Provider value={personalizedNavApi}>
                              <div className={"ssw-backdrop"} onClick={handleBlur}></div>
                              <div
                                  id={`new-suggestions-${props.view}`}
                                  className={"new-suggestions merged-personalized"}
                                  ref={touchScrollElementRef}
                              >
                                  <PersonalizedSuggestionColumns
                                      navigationRef={keyboardNavPersonalizedElementRef}
                                      isPersonalizedData={
                                          !personalizedSuggestions.isSuggestionResponse
                                      }
                                      portal={props.portal}
                                      culture={props.culture}
                                      searchTarget={props.searchTarget}
                                      trackingEndpoint={props.trackingEndpoint}
                                      articles={personalizedSuggestions.articles}
                                      flexibleSets={personalizedSuggestions.flexibleSets}
                                      storedQueries={personalizedSuggestions.storedQueries}
                                      contents={personalizedSuggestions.contents}
                                      featurePromotion={personalizedSuggestions.featurePromotion}
                                      topSearchQueries={personalizedSuggestions.topSearchQueries}
                                      priceMandantSettings={
                                          personalizedSuggestions.priceMandantSettingsArticles
                                      }
                                      priceMandantSettingsV2={
                                          personalizedSuggestions.priceMandantSettingsFlexibleSet
                                      }
                                      customerType={customerType}
                                      searchTerm={validSearchTerm}
                                      isMobileShop={isMobileShop}
                                      view={props.view}
                                      pageViewLogDataId={pageViewLogDataId}
                                      deviceScrollClass={deviceScrollClass}
                                      deviceType={device}
                                  />
                              </div>
                          </NavigationContext.Provider>
                      )
                    : showSuggestions && (
                          <NavigationContext.Provider value={navigationApi}>
                              <div className={"ssw-backdrop"} onClick={handleBlur}></div>
                              <div
                                  id={`new-suggestions-${props.view}`}
                                  className={"new-suggestions"}
                                  ref={touchScrollElementRef}
                              >
                                  <SuggestionColumns
                                      navigationRef={keyboardNavElementRef}
                                      portal={props.portal}
                                      culture={props.culture}
                                      searchTarget={props.searchTarget}
                                      trackingEndpoint={props.trackingEndpoint}
                                      articles={suggestions.articles}
                                      flexibleSets={suggestions.flexibleSets}
                                      contents={suggestions.contents}
                                      featurePromotion={suggestions.featurePromotion}
                                      topSearchQueries={suggestions.topSearchQueries}
                                      priceMandantSettings={
                                          suggestions.priceMandantSettingsArticles
                                      }
                                      priceMandantSettingsV2={
                                          suggestions.priceMandantSettingsFlexibleSet
                                      }
                                      customerType={customerType}
                                      searchTerm={validSearchTerm}
                                      isMobileShop={isMobileShop}
                                      view={props.view}
                                      pageViewLogDataId={pageViewLogDataId}
                                      deviceScrollClass={deviceScrollClass}
                                      deviceType={device}
                                  />
                              </div>
                          </NavigationContext.Provider>
                      )}

                {isWebShop && (
                    <Modal
                        open={showModal}
                        parentName={props.view}
                        tenant={props.tenant}
                        onClose={() => hideModal()}
                    />
                )}
            </div>
        </L10nContext.Provider>
    );
};

export default SearchBoxWide;
